import React, { useState } from 'react';
import { Form, Input, Button, Select, DatePicker, InputNumber, Checkbox, Divider, Col, Row } from 'antd';

const { Option } = Select;

interface AssetFormProps {
  assetData?: any;
  onSubmit: (data: any) => void;
}

const AssetForm: React.FC<AssetFormProps> = ({ assetData, onSubmit }) => {
  const [form] = Form.useForm();
  const [isSold, setIsSold] = useState(false);

  const handleFormSubmit = (values: any) => {
    onSubmit(values);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFormSubmit}
      initialValues={assetData || {}}
    >
      {/* Asset Details Section */}
      <Divider orientation="left">Asset Details</Divider>
      <Form.Item label="Asset Name" name="assetName" rules={[{ required: true, message: 'Please enter the asset name' }]}>
        <Input placeholder="Enter asset name" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Asset Type" name="assetType" rules={[{ required: true, message: 'Please select an asset type' }]}>
            <Select placeholder="Select asset type">
              <Option value="Bonds">Bonds</Option>
              <Option value="FinancialAsset">Financial Asset</Option>
              <Option value="RealEstate">Real Estate</Option>
              <Option value="FireArm">Firearm</Option>
              <Option value="FarmTool">Farm Tool</Option>
                  <Option value="FarmTool">Farm Tool</Option>
                  <Option value="Cash">Cash</Option>
                  <Option value="Investment">Investment</Option>
                  <Option value="Other">Other</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Geo Location" name="geoLocation" rules={[{ required: true, message: 'Please enter the location' }]}>
            <Input placeholder="Enter geographic location" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Description" name="description" rules={[{ required: true, message: 'Please provide a description' }]}>
        <Input.TextArea placeholder="Enter description, e.g., identifier, coordinates, serial number" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="Covered in Will?" name="isCoveredInWill" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Jointly Owned?" name="isJointlyOwned" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="EPTL Exemption?" name="eptlExemption" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Small Estate?" name="isSmallEstate" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Col>
      </Row>

      {/* Portfolio Section */}
      <Divider orientation="left">Portfolio Section</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Property Type" name="propertyType" rules={[{ required: true, message: 'Please select a property type' }]}>
            <Select placeholder="Select property type">
              <Option value="Equity">Equity</Option>
              <Option value="RealEstate">Real Estate</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Equity/Property" name="equityProperty" rules={[{ required: true, message: 'Please specify the equity or property' }]}>
            <Input placeholder="e.g., 10 Year Bond Funds" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="Ticker (optional)" name="ticker">
            <Input placeholder="e.g., MMM" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Original Quantity Owned" name="originalQuantity" rules={[{ required: true, message: 'Please enter the quantity' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Prior Day Closing Price" name="priorDayClosingPrice">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="Current Quantity Owned" name="currentQuantity">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Date of Purchase" name="dateOfPurchase">
            <DatePicker format="MM/DD/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Cost Basis" name="costBasis">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Is on Lease?" name="isOnLease" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Liens/Fees from Bank/Broker Statement" name="liensFees">
            <Input.TextArea placeholder="Details of any liens or fees" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Net Liens/Lease Value" name="netLiensValue">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Value Source" name="valueSource">
            <Input placeholder="e.g., Investing.com" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="Discount to Market" name="discountToMarket">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Discount to Family" name="discountToFamily">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Is Item Sold?" name="isItemSold" valuePropName="checked">
            <Checkbox onChange={(e) => setIsSold(e.target.checked)} />
          </Form.Item>
        </Col>
      </Row>

      {isSold && (
        <Form.Item label="Cash Received if Sold" name="cashReceivedIfSold">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      )}

      <Form.Item label="Donated/Thrown Away/Given To Friend?" name="donationDetails">
        <Input.TextArea placeholder="Details if donated, discarded, or given away" />
      </Form.Item>

      {/* Submit Button */}
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
};

// Usage
const AssetPage: React.FC = () => {
  const handleSubmit = (data: any) => {
    // You could add API integration here
  };

  return (<div style={{ padding: 24, minHeight: 360, background: '#ffffff', borderRadius: '8px' }}> 
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px' }}>
        
      <h2>Add or Edit Asset</h2>
      <AssetForm onSubmit={handleSubmit} />
      </div>
    </div>
  );
};

export default AssetPage;
